import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { environment } from '@employer/environments/environment'
import { RestApiClient } from '@engineering11/web-api-rest'
import { BehaviorSubject, firstValueFrom, filter, Subscription, take, timeout } from 'rxjs'
import { DEFAULT_NETWORK_MONITORING_CONFIG, getBaseSuffix, TokenStorage } from 'shared-lib'
import {
  ClientToolName,
  ClientToolOutcome,
  getClientToolCall,
  IClientToolArgs,
  IClientToolCall,
  toNavigateCommands,
} from '../models/assistant-client-tool.model'
import { ConversationStatus, IAssistantRunStep } from '../models/assistant.model'
import { AssistantConversationService } from './assistant-conversation.service'
import { GuideService } from './guide.service'
import { TourEngineService } from './tour-engine.service'

// BACKEND CONTRACT (assumption) — see ../BACKEND-CONTRACT.md
// Tool-result route under `environment.services.assistant` (URI-versioned, ids in path).
// Reconciled 2026-08-12: POST v1/conversations/:conversationId/steps/:stepId/result, body { outcome }.
const toolResultPath = (conversationId: string, stepId: string) => `conversations/${conversationId}/steps/${stepId}/result`

/** A viewport-space rect the highlight overlay draws its spotlight box from. */
export interface HighlightRect {
  top: number
  left: number
  width: number
  height: number
}

/**
 * Raw `RestApiClient` errors are `E11ApiError` (an AxiosError whose `response.data` carries a
 * `statusCode`). `isE11ApiError` is not re-exported from `@engineering11/web-api-rest`'s public entry, so
 * we replicate its narrow inline rather than deep-import (mirrors the 4.2 send / 4.8 confirm seams).
 * A 409 is the busy/conflict signal; we key on the status alone (body `statusCode` OR the HTTP
 * `response.status`) so a 409 whose body omits it is still swallowed (CAP-3).
 */
function isBusyConflict(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { statusCode?: number } } })?.response
  return response?.data?.statusCode === 409 || response?.status === 409
}

/**
 * Client-tool executor (Epic 4, story 4.9). Listener-driven: `connect()` subscribes the story-4.2
 * `AssistantConversationService.steps$` stream (the live conversation-doc listener), dedupes each
 * `client_tool_call` step by id (executed at most once despite Firestore re-emits), dispatches
 * `navigate` / `highlight` / `startTour` to UI-only handlers, and POSTs the outcome through the same
 * `RestApiClient` seam the conversation service uses (jobsAssistant, `409` swallowed) so the run advances.
 *
 * CAP-3 (no mutation): the only write this service makes is the sanctioned tool-result POST; its
 * collaborators are `Router` (navigate — guards apply), `DOCUMENT` (highlight anchor resolution),
 * `TourEngineService` (`urlMatchesTarget` reuse), `GuideService` (startTour launches the in-pane
 * walkthrough), and the `steps$` listener. It injects no domain repository/mutation client.
 *
 * Plus-gating is structural: `connect()` is called only from `<assistant-bar-active>.ngOnInit`, and the
 * command bar instantiates that composer only in its entitled (Cnect Plus) branch (story 4.3). A Default
 * user never constructs it, so `connect()` never runs — no listener, no client tool, no POST.
 */
@Injectable({ providedIn: 'root' })
export class AssistantClientToolsService {
  /** Max anchor-resolution polls before `highlight` treats the anchor as absent (bounded — never hangs). */
  private static readonly MAX_ANCHOR_ATTEMPTS = 20
  /** How long a highlight spotlight lingers before it auto-clears (one-off cue, not a persistent overlay). */
  private static readonly HIGHLIGHT_DURATION_MS = 4000
  /**
   * The backend writes the client-tool STEP (`awaiting_client`) while the run is still invoking, but only parks
   * the CONVERSATION into `awaiting_client` in a follow-up write once the run returns. A result posted in that
   * window is rejected as superseded (409, swallowed) and the run hangs. Wait for the parked status before
   * posting, bounded so a park that never lands still posts and lets the backend decide.
   */
  private static readonly PARK_WAIT_MS = 8000

  private restClient: RestApiClient

  /** Step ids already dispatched — marked BEFORE dispatch so a listener re-emit re-executes nothing. */
  private readonly processed = new Set<string>()

  /** The run whose `pendingNavigation` was already acted on, so a conversation-doc re-emit navigates at most once. */
  private lastNavigatedRunId?: string

  private readonly highlightSubject = new BehaviorSubject<HighlightRect | null>(null)
  /** The active highlight spotlight rect (or `null` when nothing is spotlit). The overlay renders off this. */
  readonly highlight$ = this.highlightSubject.asObservable()

  private clearTimer: ReturnType<typeof setTimeout> | null = null

  constructor(
    private router: Router,
    private conversationService: AssistantConversationService,
    private tourEngine: TourEngineService,
    @Inject(DOCUMENT) private document: Document,
    tokenStorage: TokenStorage,
    private guide: GuideService
  ) {
    this.restClient = new RestApiClient({
      // Reconciled 2026-08-12: assistant is its own service (service-assistant at /assistant).
      baseUrl: environment.services.assistant,
      token: () => tokenStorage.getAccessToken(),
      baseSuffix: getBaseSuffix(environment, 'assistant'),
      ...DEFAULT_NETWORK_MONITORING_CONFIG,
    })
  }

  /**
   * Subscribe the live `steps$` stream (dispatching each new `client_tool_call` step exactly once) and the live
   * `conversation$` stream (auto-navigating on a new `pendingNavigation`). The caller (`<assistant-bar-active>`)
   * owns the returned `Subscription` and tears both down on destroy.
   */
  connect(): Subscription {
    const subscription = new Subscription()
    subscription.add(
      this.conversationService.steps$.subscribe(steps => {
        // Read the active conversation at dispatch time so the dedupe key is scoped per conversation — a
        // reused step id in a different conversation must not be silently swallowed. A steps$ emission
        // without a conversation id cannot be correlated, so skip it (no dispatch, no dedupe entry).
        const conversationId = this.conversationService.activeConversationId
        if (!conversationId) {
          return
        }
        for (const step of steps) {
          const call = getClientToolCall(step)
          const key = `${conversationId}:${step.id}`
          if (!call || this.processed.has(key)) {
            continue
          }
          // Mark processed BEFORE dispatch: the Firestore listener re-emits the whole doc on every change,
          // so a mid-flight re-emit of this same (async) step must not re-execute or re-POST it (CAP-1).
          this.processed.add(key)
          void this.execute(step, call)
        }
      })
    )
    // Deterministic follow-along: the backend sets `pendingNavigation` after a run whose write named a screen, so
    // the panel navigates the user there without the model having to volunteer a navigate call. Deduped by
    // `runId` so a Firestore re-emit of the same conversation doc navigates at most once.
    subscription.add(
      this.conversationService.conversation$.subscribe(conversation => {
        const pending = conversation?.pendingNavigation
        if (!pending || pending.runId === this.lastNavigatedRunId) {
          return
        }
        this.lastNavigatedRunId = pending.runId
        void this.autoNavigate(pending.route)
      })
    )
    return subscription
  }

  /** One-way navigation to a backend-supplied route (already id-filled). No tool-result POST — it is a hint, not a step. */
  private async autoNavigate(route: string): Promise<void> {
    if (!route || this.hasUnfilledPlaceholder(route)) {
      return
    }
    try {
      await this.router.navigateByUrl(route)
    } catch {
      // A guard redirect or a bad route is not worth surfacing: the panel's answer already told the user what happened.
    }
  }

  /** Run the matching handler (catching any throw as `error`) then report the outcome. */
  private async execute(step: IAssistantRunStep, call: IClientToolCall): Promise<void> {
    let outcome: ClientToolOutcome
    try {
      outcome = await this.dispatch(call)
    } catch {
      // A handler that rejects unexpectedly reports `error`; the executor keeps running for later steps.
      outcome = ClientToolOutcome.Error
    }
    // Post only once the conversation is actually parked — the step becomes visible before the park write lands,
    // and a result posted into that gap 409s as superseded and the run hangs.
    await this.awaitParked()
    await this.postResult(step.id, outcome)
  }

  /** Resolve once the conversation is parked on the client step, or after a bound so a missed park still posts. */
  private async awaitParked(): Promise<void> {
    try {
      await firstValueFrom(
        this.conversationService.status$.pipe(
          filter(status => status === ConversationStatus.AwaitingClient),
          take(1),
          timeout(AssistantClientToolsService.PARK_WAIT_MS)
        )
      )
    } catch {
      // Park not observed within the window: fall through and post anyway, letting the backend decide.
    }
  }

  private dispatch(call: IClientToolCall): Promise<ClientToolOutcome> {
    const args = call.args ?? {}
    switch (call.name) {
      case ClientToolName.Navigate:
        return this.navigate(args)
      case ClientToolName.Highlight:
        return this.highlight(args)
      case ClientToolName.StartTour:
        return this.startTour()
      default:
        return Promise.resolve(ClientToolOutcome.Blocked)
    }
  }

  /**
   * `navigate` through the Angular `Router` so route guards apply. Success ONLY when navigation resolves
   * truthy AND the resulting URL matches the target (`urlMatchesTarget`); a `false` result, a thrown nav
   * error, or a guard redirect to a different URL all mean `blocked` — reported, never bypassed (CAP-2).
   */
  private async navigate(args: IClientToolArgs): Promise<ClientToolOutcome> {
    const target = args.target
    // An empty/undefined target has nothing to correlate — block without touching the router.
    if (target === undefined || target === '' || (Array.isArray(target) && target.length === 0)) {
      return ClientToolOutcome.Blocked
    }
    // A route still carrying a `:placeholder` segment (e.g. `/jobs/post/:id`) is a template the assistant failed
    // to fill with a real id — it can only 404. Block it so the user is never dumped on a broken page; the
    // assistant reports it could not open the screen instead.
    if (this.hasUnfilledPlaceholder(target)) {
      return ClientToolOutcome.Blocked
    }
    try {
      // A string target rides `navigateByUrl` so query/fragment parse correctly; an array uses `navigate`.
      if (typeof target === 'string') {
        await this.router.navigateByUrl(target)
      } else {
        await this.router.navigate(toNavigateCommands(target))
      }
    } catch {
      // A thrown navigation is not itself the verdict — success is decided by where we actually ended up.
    }
    // Success is "we are at the target", not "a navigation occurred": navigating to the page the user is already
    // on returns false from the router but still leaves them at the target, which is a success, not a block. A
    // guard redirect or a failed navigation leaves the URL not matching, which is the block.
    return this.tourEngine.urlMatchesTarget(target) ? ClientToolOutcome.Success : ClientToolOutcome.Blocked
  }

  /** A `:name` segment in a string route, or a command that is a bare `:name`, is an unfilled route template. */
  private hasUnfilledPlaceholder(target: string | any[]): boolean {
    return typeof target === 'string' ? /(^|\/):[^/]+/.test(target) : target.some(segment => typeof segment === 'string' && segment.startsWith(':'))
  }

  /**
   * `highlight` resolves a `[data-tour-id]` anchor via a bounded render wait, measures it, and drives the
   * standalone spotlight overlay (design-contract §Surface 3). Anchor absent or a zero-size box → `blocked`.
   */
  private async highlight(args: IClientToolArgs): Promise<ClientToolOutcome> {
    const anchor = args.anchor
    if (!anchor) {
      return ClientToolOutcome.Blocked
    }
    const el = await this.resolveAnchor(anchor)
    if (!el) {
      return ClientToolOutcome.Blocked
    }
    el.scrollIntoView?.({ block: 'center', inline: 'center' })
    const rect = el.getBoundingClientRect()
    // An anchor present but with no laid-out box (e.g. display:none) yields a zero rect — treat as blocked
    // rather than drawing a zero-size spotlight.
    if (rect.width === 0 || rect.height === 0) {
      return ClientToolOutcome.Blocked
    }
    this.highlightSubject.next({ top: rect.top, left: rect.left, width: rect.width, height: rect.height })
    this.scheduleHighlightClear()
    return ClientToolOutcome.Success
  }

  /** `startTour` delegates to the floating guided walkthrough card (`GuideService`) — reuse it, do not re-implement. */
  private async startTour(): Promise<ClientToolOutcome> {
    void this.guide.start()
    return ClientToolOutcome.Success
  }

  /**
   * POST the tool result so the run advances. No-op unless a conversation is selected (no id to correlate).
   * A `409` busy/conflict is swallowed and never surfaced; a non-409 is logged, not surfaced (CAP-3).
   */
  private postResult(stepId: string, outcome: ClientToolOutcome): Promise<void> {
    const conversationId = this.conversationService.activeConversationId
    if (!conversationId) {
      return Promise.resolve()
    }
    // Reconciled 2026-08-12: conversationId + stepId are path params; body carries outcome only.
    return new Promise<void>(resolve => {
      this.restClient.post<void>(toolResultPath(conversationId, stepId), { outcome }).subscribe({
        error: (error: unknown) => {
          if (!isBusyConflict(error)) {
            // eslint-disable-next-line no-console
            console.error('[assistant] client-tool result POST failed', error)
          }
          resolve()
        },
        complete: () => resolve(),
      })
    })
  }

  /**
   * Poll `document.querySelector('[data-tour-id="…"]')` a bounded number of times (mirrors
   * `TourEngineService.resolveAnchor`) so a not-yet-rendered anchor gets a brief chance to appear, then
   * resolves `null`. Never hangs.
   */
  private resolveAnchor(anchor: string): Promise<HTMLElement | null> {
    const selector = `[data-tour-id="${anchor}"]`
    return new Promise<HTMLElement | null>(resolve => {
      let attempts = 0
      const tick = () => {
        const el = this.document.querySelector(selector) as HTMLElement | null
        if (el) {
          resolve(el)
          return
        }
        if (++attempts >= AssistantClientToolsService.MAX_ANCHOR_ATTEMPTS) {
          resolve(null)
          return
        }
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(tick)
        } else {
          setTimeout(tick, 16)
        }
      }
      tick()
    })
  }

  /** Auto-dismiss the one-off spotlight after a bounded duration (it is a cue, not a persistent overlay). */
  private scheduleHighlightClear(): void {
    if (this.clearTimer) {
      clearTimeout(this.clearTimer)
    }
    this.clearTimer = setTimeout(() => this.highlightSubject.next(null), AssistantClientToolsService.HIGHLIGHT_DURATION_MS)
  }
}
